import { CalendarDate, parseDate } from '@internationalized/date';
import { keyBy, last } from 'lodash/fp';
import { useState } from 'react';
import { components as ReactSelectComponents, OptionProps } from 'react-select';
import { FormError } from 'shared/components/FormControl';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { CaseDataManager } from 'dashboard/models/case';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import Checkbox from 'design-system/components/Checkbox';
import DatePicker from 'design-system/components/DatePicker';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import {
  FIELD_SCHEMA_TYPE_DISPLAY,
  FieldSchema,
  FieldSchemaChoices,
  FieldSchemaType,
} from 'lib/fields';

const ENABLED_FIELD_SCHEMA_TYPES = [
  FieldSchemaType.Boolean,
  FieldSchemaType.Choices,
  FieldSchemaType.Date,
  FieldSchemaType.Number,
  FieldSchemaType.Integer,
  FieldSchemaType.String,
];

export interface FieldUpdateProps {
  fieldSchemas: FieldSchema[];
  caseDataManager: CaseDataManager;
  updateField: (fieldsToUpdate: Record<string, unknown>) => void;
}

const FieldUpdate = ({ fieldSchemas, caseDataManager, updateField }: FieldUpdateProps) => {
  const [fieldKey, setFieldKey] = useState<string>('');
  const [fieldValue, setFieldValue] = useState<unknown>(null);

  const handleUpdateField = () => {
    updateField({ [fieldKey]: fieldValue });
  };

  const handleUpdateFieldKey = (fieldKey: string) => {
    setFieldKey(fieldKey);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setFieldValue(caseDataManager.case.fields?.[fieldKey.split('_').join('-')]);
  };

  const fieldSchemaMap = keyBy('key', fieldSchemas);
  const fieldSchemaOptions = fieldSchemas.map((f) => {
    return {
      value: f.key,
      label: f.label,
      fieldType: f.type,
      disabled: !ENABLED_FIELD_SCHEMA_TYPES.includes(f.type),
    };
  });

  if (fieldSchemas.length === 0) {
    return (
      <Card>
        <Card.Body>
          <Heading size='sm'>Update fields</Heading>
          <Paragraph>
            Add a field to your case template to get started. Once you add a field, you can update
            it here.
          </Paragraph>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <Heading size='sm'>Update fields</Heading>
        <Label id='field-update__selector-label' size='md' weight='bold'>
          Field
        </Label>
        <ReactSelect<string, OptionType<string> & { fieldType: FieldSchemaType; label: string }>
          id='field-update__field-key-selector'
          aria-labelledby='field-update__selector-label'
          options={fieldSchemaOptions}
          placeholder='Please select a field.'
          value={fieldKey}
          onChange={handleUpdateFieldKey}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          components={{ Option: FieldKeyOption }}
          portal
        />
        {fieldKey && (
          <>
            <Label id='field-update__value-label' size='md' weight='bold'>
              Value
            </Label>
            <FieldValueInput
              key={`field-value-input__${fieldKey}-${fieldSchemaMap[fieldKey].type}`}
              value={fieldValue}
              fieldKey={fieldKey}
              fieldSchemaMap={fieldSchemaMap}
              caseDataManager={caseDataManager}
              aria-labelledby='field-update__value-label'
              onChange={setFieldValue}
            />
          </>
        )}
        <Spacer height={1} />
        <HStack>
          <Button
            id='field-update__update-field-button'
            disabled={fieldKey == null || fieldValue == null}
            onClick={() => handleUpdateField()}
          >
            Update field
          </Button>
        </HStack>
      </Card.Body>
    </Card>
  );
};

const FieldValueInput = ({
  value,
  fieldKey,
  fieldSchemaMap,
  onChange,
  'aria-labelledby': labelledBy,
}: {
  value: unknown;
  fieldKey: string;
  fieldSchemaMap: Record<string, FieldSchema>;
  caseDataManager: CaseDataManager;
  onChange: (value: unknown) => void;
  'aria-labelledby': string;
}) => {
  const [error, setError] = useState<string | undefined>(undefined);

  const fieldSchema = fieldSchemaMap[fieldKey];
  const fieldSchemaType = fieldSchema.type;

  switch (fieldSchemaType) {
    case FieldSchemaType.String:
      return (
        <Input
          value={value as string}
          type='text'
          aria-labelledby={labelledBy}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case FieldSchemaType.Number: {
      const min = fieldSchema.config.min;
      const max = fieldSchema.config.max;
      return (
        <Input
          value={value as number}
          type='number'
          aria-labelledby={labelledBy}
          onChange={(e) => {
            const nextVal = Number(e.target.value);
            setError(validateMinMax({ nextVal, min, max }));
            onChange(nextVal);
          }}
        />
      );
    }
    case FieldSchemaType.Integer: {
      const min = fieldSchema.config.min;
      const max = fieldSchema.config.max;
      return (
        <>
          <Input
            value={value as number}
            type='number'
            aria-labelledby={labelledBy}
            onChange={(e) => {
              const nextVal = Number(e.target.value);
              if (Math.floor(nextVal) !== nextVal) {
                setError('Must input an integer.');
              } else {
                setError(validateMinMax({ nextVal, min, max }));
              }

              onChange(nextVal);
            }}
          />
          {error && <FormError>{error}</FormError>}
        </>
      );
    }
    case FieldSchemaType.Boolean:
      return (
        <Checkbox
          value={value}
          aria-labelledby={labelledBy}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    case FieldSchemaType.Choices: {
      const selectedFieldSchema = fieldSchemaMap[fieldKey] as FieldSchemaChoices;
      const configOptions = (selectedFieldSchema.config?.['options'] ?? []).map((option, idx) => {
        const label = selectedFieldSchema.config['option-labels']
          ? selectedFieldSchema.config['option-labels'][idx]
          : option;
        return {
          label: label,
          value: option,
        };
      });
      const options = selectedFieldSchema.config['allow-empty']
        ? [{ label: 'Select...', value: '' }, ...configOptions]
        : configOptions;
      return (
        <ReactSelect
          placeholder='Select...'
          aria-labelledby={labelledBy}
          onChange={onChange}
          value={value as string}
          options={options}
          portal
        />
      );
    }
    case FieldSchemaType.Date: {
      const dateValue = value ? parseDate(value as string) : null;
      const onChangeDate = (nextDate: CalendarDate | null) => {
        onChange(nextDate?.toString() ?? null);
      };
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return <DatePicker value={dateValue} aria-labelledby={labelledBy} onChange={onChangeDate} />;
    }
    default:
      return (
        <Paragraph color='secondary'>
          Updating this field type is not supported by this module.
        </Paragraph>
      );
  }
};

const FieldKeyOption = ({
  ...props
}: OptionProps<{ fieldType: FieldSchemaType; label: string }>) => {
  const id =
    props.selectProps.id != null
      ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        `${props.selectProps.id}-option-${last(props.innerProps.id.split('-'))}`
      : undefined;
  const selectionText = `Type: ${
    FIELD_SCHEMA_TYPE_DISPLAY[props.data.fieldType as FieldSchemaType] || 'Unknown'
  }`;
  const textColor = props.isDisabled ? 'disabled' : 'secondary';

  return (
    <ReactSelectComponents.Option {...props} innerProps={{ ...props.innerProps, id }}>
      <div>
        <Paragraph color={textColor} size='sm' style={{ lineBreak: 'anywhere' }}>
          {props.data.label}
        </Paragraph>
        <Paragraph color={textColor} size='xs'>
          {selectionText}
        </Paragraph>
      </div>
    </ReactSelectComponents.Option>
  );
};

const validateMinMax = ({
  nextVal,
  min,
  max,
}: {
  nextVal: number;
  min: number | undefined;
  max: number | undefined;
}) => {
  if ((min && nextVal < min) || (max && nextVal > max)) {
    if (min != null && max != null) {
      return `Value must be between ${min} and ${max}.`;
    } else if (min) {
      return `Value must be at least ${min}.`;
    } else if (max) {
      return `Value must be at most ${max}.`;
    }
  }
};

export default FieldUpdate;
